import { useCallback, useEffect, useState } from "react";
import { FileText, Loader2, Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";

const SKILLS = [
  ["interest_motivation", "Interest & motivation to travel independently"],
  ["learning_retention", "Learning & retaining new information"],
  ["verbal_direction", "Following verbal directions"],
  ["attention", "Attention & concentration"],
  ["problem_solving", "Problem solving"],
  ["emergencies", "Knowing what to do in an emergency"],
  ["help_seeking", "Asking for help when needed"],
  ["communication_unfamiliar_people", "Communicating with unfamiliar people"],
  ["stranger_social_behaviour", "Stranger awareness & social behaviour"],
  ["timekeeping", "Timekeeping / telling the time"],
  ["colours_numbers", "Recognising colours & numbers"],
  ["money_fares_oyster", "Money, fares & Oyster/Zip card"],
  ["mobile_phone", "Using a mobile phone"],
  ["possessions_personal_safety", "Looking after possessions & personal safety"],
  ["weather_clothing_hazards", "Weather, clothing & hazard awareness"],
  ["distractions_vulnerability", "Distractions & vulnerability awareness"],
];

const SECTION_GROUPS = [
  ["Medical & physical", [
    ["medical_conditions", "Medical conditions"],
    ["mobility", "Mobility"],
    ["steps_stairs", "Steps & stairs"],
    ["visual_hearing", "Visual / hearing"],
    ["medications", "Medications"],
    ["medication_side_effects", "Medication side effects"],
    ["medication_timing", "Medication timing during journeys"],
    ["active_seizures", "Active seizures"],
    ["allergies_phobias", "Allergies & phobias"],
  ]],
  ["Travel experience", [
    ["prior_bus_experience", "Prior bus experience"],
    ["prior_train_experience", "Prior train / underground experience"],
    ["prior_local_travel", "Prior local independent travel"],
    ["has_school_id", "Has school ID"],
  ]],
  ["Barriers & support", [
    ["reasons_not_independent", "Reasons independent travel has not happened"],
    ["family_capacity", "Family capacity to support practice"],
  ]],
  ["Coping", [
    ["distress_triggers", "Distress triggers"],
    ["observable_distress_signs", "Observable signs of distress"],
  ]],
];

const EMPTY = { assessment_date: "", referral_source: "", skills: {}, sections: {} };

export default function PreAssessmentTab({ child }) {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("assessments:create");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pre-assessments?child_id=${child.id}`);
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => { load(); }, [load]);

  const open = (pre) => {
    setSelected(pre);
    setForm({
      assessment_date: pre.assessment_date || "",
      referral_source: pre.referral_source || "",
      skills: pre.skills || {},
      sections: pre.sections || {},
    });
  };

  const setSkill = (key, field, value) =>
    setForm((f) => ({ ...f, skills: { ...f.skills, [key]: { ...(f.skills[key] || {}), [field]: value } } }));
  const setSection = (key, value) =>
    setForm((f) => ({ ...f, sections: { ...f.sections, [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      if (selected && selected !== "new") {
        await api.patch(`/pre-assessments/${selected.id}`, form);
        toast.success("Pre-assessment saved");
      } else {
        const { data } = await api.post(`/pre-assessments/children/${child.id}`, form);
        toast.success(`Pre-assessment ${data.pre_assessment.pre_number} created`);
      }
      setSelected(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const complete = async () => {
    setSaving(true);
    try {
      await api.patch(`/pre-assessments/${selected.id}`, form);
      await api.post(`/pre-assessments/${selected.id}/complete`);
      toast.success("Pre-assessment completed");
      setSelected(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-600" data-testid="pre-assessment-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const editing = selected === null ? false : selected.status !== "completed";
  const showForm = selected || items.length === 0;

  return (
    <div className="space-y-4" data-testid="pre-assessment-tab">
      {!showForm && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Travel pre-assessments</CardTitle>
            {canCreate && (
              <Button variant="outline" className="h-11" data-testid="pre-assessment-new-button" onClick={() => { setForm(EMPTY); setSelected("new"); }}>
                <Plus className="h-4 w-4 mr-1" /> New pre-assessment
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            {items.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3" data-testid={`pre-assessment-row-${p.id}`}>
                <p className="font-mono text-sm font-semibold text-slate-800">{p.pre_number}</p>
                <Badge className={p.status === "completed" ? "bg-ukgreen-light text-ukgreen border-transparent" : "bg-amber-50 text-amber-700 border-transparent"} data-testid={`pre-assessment-status-${p.id}`}>
                  {p.status === "completed" ? "Completed" : "Draft"}
                </Badge>
                <span className="text-xs text-slate-500">{p.assessment_date || "No date"} · {p.assessor_name}</span>
                <div className="ml-auto flex gap-2">
                  <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid={`pre-assessment-open-${p.id}`} onClick={() => open(p)}>
                    {p.status === "completed" ? "View" : "Edit"}
                  </Button>
                  <a href={`${API_URL}/pre-assessments/${p.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`pre-assessment-pdf-${p.id}`}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <FileText className="h-3.5 w-3.5" /> PDF
                  </a>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {showForm && selected !== "new" && items.length > 0 && (
        <Button variant="ghost" className="h-9 text-xs" data-testid="pre-assessment-back-list" onClick={() => { setSelected(null); setForm(EMPTY); }}>
          ← Back to list
        </Button>
      )}

      {(showForm || selected === "new") && (
        <Card data-testid="pre-assessment-form">
          <CardHeader>
            <CardTitle className="text-base">
              {selected && selected !== "new" ? `${selected.pre_number} — Travel pre-assessment` : "New travel pre-assessment"}
            </CardTitle>
            <p className="text-xs text-slate-500">
              Completed with the trainee and family before the initial suitability assessment. This record informs — it does not decide — suitability.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Assessment date</Label>
                <Input type="date" className="h-11" data-testid="pre-assessment-date" value={form.assessment_date}
                  disabled={!editing && selected !== "new" && !!selected}
                  onChange={(e) => setForm((f) => ({ ...f, assessment_date: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Referral source</Label>
                <Input className="h-11" data-testid="pre-assessment-referral-source" value={form.referral_source}
                  disabled={!editing && selected !== "new" && !!selected}
                  onChange={(e) => setForm((f) => ({ ...f, referral_source: e.target.value }))} />
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Prerequisite skills</h3>
              <div className="space-y-3">
                {SKILLS.map(([key, label]) => {
                  const s = form.skills[key] || {};
                  const ro = !editing && selected !== "new" && !!selected;
                  return (
                    <div key={key} className="rounded-md border border-border p-3 space-y-2" data-testid={`pre-skill-${key}`}>
                      <p className="text-sm font-medium text-slate-800">{label}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Select value={s.response || ""} disabled={ro} onValueChange={(v) => setSkill(key, "response", v)}>
                          <SelectTrigger className="h-10" data-testid={`pre-skill-response-${key}`}><SelectValue placeholder="Yes / No / Partly" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                            <SelectItem value="partly">Partly</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input className="h-10" placeholder="Comment" value={s.comment || ""} disabled={ro}
                          data-testid={`pre-skill-comment-${key}`} onChange={(e) => setSkill(key, "comment", e.target.value)} />
                        <Input className="h-10" placeholder="Strategy" value={s.strategy || ""} disabled={ro}
                          data-testid={`pre-skill-strategy-${key}`} onChange={(e) => setSkill(key, "strategy", e.target.value)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {SECTION_GROUPS.map(([group, fields]) => (
              <div key={group}>
                <h3 className="font-semibold text-slate-900 mb-2">{group}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {fields.map(([key, label]) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs">{label}</Label>
                      <Textarea rows={2} value={form.sections[key] || ""} data-testid={`pre-section-${key}`}
                        disabled={!editing && selected !== "new" && !!selected}
                        onChange={(e) => setSection(key, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              {(canCreate && (editing || selected === "new" || !selected)) && (
                <>
                  <Button className="h-11" data-testid="pre-assessment-save" disabled={saving} onClick={save}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  {selected && selected !== "new" && selected.status !== "completed" && (
                    <Button variant="outline" className="h-11 border-ukgreen text-ukgreen" data-testid="pre-assessment-complete" disabled={saving} onClick={complete}>
                      Mark as completed
                    </Button>
                  )}
                </>
              )}
              {selected && selected !== "new" && (
                <a href={`${API_URL}/pre-assessments/${selected.id}/pdf`} target="_blank" rel="noreferrer" data-testid="pre-assessment-pdf-open"
                  className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover">
                  <FileText className="h-4 w-4" /> PDF
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {!showForm && items.length === 0 && null}
      {items.length === 0 && !showForm && (
        <p className="text-sm text-slate-600" data-testid="pre-assessment-empty">No pre-assessments recorded.</p>
      )}
    </div>
  );
}
